// @ts-check
import { useAnalytic } from '@skooldio/analytics-provider';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useConfig } from 'Context/ConfigContext';
import useCapacitorStorage from 'Util/Hooks/useCapacitorStorage';
import { useCapacitor } from 'Util/Capacitors';
import useMyExternalProfile from 'Routes/Classroom/Domain/useMyExternalProfile';
import { myProfile, isLoading } from 'Redux/user-profile/selectors';

import sendMPEvent from './sendMPEvent';
import useMeasurementProtocolSession from './useMPSession';
import createMeasurementProtocolEvent from './createMPEventData';

// for anywhere app: GTM and GA4 not working on electron and iOS. so use measurement protocol as alternative
// https://issuetracker.google.com/issues/174954288
function MeasurementProtocolListener({ user, isUserLoading }) {
  /** @type {{ measurementProtocol: MeasurementProtocolConfig, appVersion: string }} */
  const { measurementProtocol, appVersion } = useConfig();
  const { ga } = useAnalytic();
  const { deviceInfo } = useCapacitor();
  const location = useLocation();

  const [clientId, setClientId] = useCapacitorStorage(
    /** @type {string | null} */ (null),
    'custom_ga_client_id'
  );
  const getSession = useMeasurementProtocolSession();
  const { data: externalProfile, loading: externalProfileLoading } = useMyExternalProfile({
    skip: !user,
  });

  useEffect(() => {
    if (deviceInfo && deviceInfo.uuid && !clientId) {
      setClientId(deviceInfo.uuid);
    }
  }, [deviceInfo]);

  /** @param {MeasurementProtocolEvent} eventData */
  const sendEvent = (eventData) => {
    // recommended parameters [doc: https://developers.google.com/analytics/devguides/collection/protocol/ga4/sending-events?client_type=gtag#recommended_parameters_for_reports]
    const eventWithUserActivityParams = {
      name: eventData.name,
      params: { ...eventData.params, engagement_time_msec: 100, session_id: getSession().id },
    };

    const academy = user?.additional7;
    const provider = externalProfile?.myExternalProfile?.providerName;
    sendMPEvent({
      clientId: ga.ga()?.getAll?.()?.[0]?.get?.('clientId') ?? clientId,
      userId: user?.id,
      userProperties: {
        ...(academy ? { academy: { value: academy } } : {}),
        ...(provider ? { provider: { value: provider } } : {}),
        device: {
          value: `${deviceInfo.model}-${deviceInfo.platform}-${deviceInfo.operatingSystem}`,
        },
        app_version: {
          value: deviceInfo?.appVersion !== '-' ? deviceInfo.appVersion : appVersion,
        },
      },
      events: [eventWithUserActivityParams],
    });
  };

  /** @param {MessageEvent<Message>} messageEvent */
  const handleMessageEvent = (messageEvent) => {
    if (!messageEvent.data.topic) return;

    const eventData = createMeasurementProtocolEvent(messageEvent.data);
    if (!eventData) return;

    sendEvent(eventData);
  };

  /** @param {string} currentPathname */
  const sendPageViewEvent = (currentPathname) => {
    const href = window.location.href;
    const url = new URL(currentPathname, href);
    const pageTitle = window.document.title;
    sendEvent({
      name: 'page_view',
      params: { page_location: url.toString(), page_title: pageTitle },
    });
  };

  useEffect(() => {
    if (!measurementProtocol?.id) return;

    window.addEventListener('message', handleMessageEvent);

    return () => {
      window.removeEventListener('message', handleMessageEvent);
    };
  }, [handleMessageEvent]);

  useEffect(() => {
    if (!measurementProtocol?.id) return;
    if (!isUserLoading && !externalProfileLoading) sendPageViewEvent(location.pathname);
  }, [location, isUserLoading, externalProfileLoading]);

  return null;
}

export default connect(
  (state) => ({ user: myProfile(state), isUserLoading: isLoading(state) }),
  {}
)(MeasurementProtocolListener);

/** @typedef {import('Util/Config').MeasurementProtocolConfig} MeasurementProtocolConfig */
/** @typedef {import('Util/Message/postMessageHelper').Message} Message */
/** @typedef {import('./sendMPEvent').MeasurementProtocolEvent} MeasurementProtocolEvent} */
